import { cleanInvoice } from '@spike/invoices-action';
import { showError } from '@spike/notifications-action';
import Checkout from 'components/Checkout2/Checkout';
import InvoiceDetail from 'components/Payments/Transactions/Invoices/InvoiceDetail';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { ReactNode, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

interface SelectedInvoiceDetail {
    invoiceId: number;
    marketplaceId: number;
    marketplaceName: string;
}

interface UseInvoiceDetailViewOptions {
    onClosed?: () => void;
    zIndex?: number;
}

export const useInvoiceDetailView = (options?: UseInvoiceDetailViewOptions) => {
    const dispatch = useDispatch();
    const { getOrganizationMarketplace } = useOrganizationMarketplaces();
    const [selectedInvoiceDetail, setSelectedInvoiceDetail] = useState<SelectedInvoiceDetail | null>(null);
    const [showCheckout, setShowCheckout] = useState<{
        orderId?: number;
        bookingId?: number;
        hide: boolean;
    } | null>(null);
    const returnToInvoiceDetailRef = useRef<SelectedInvoiceDetail | null>(null);
    const isCheckoutOpen = showCheckout !== null;

    const openInvoiceDetail = (invoiceId: number | null | undefined, marketplaceId: number) => {
        if (!invoiceId) {
            return;
        }

        setSelectedInvoiceDetail({
            invoiceId,
            marketplaceId,
            marketplaceName: getOrganizationMarketplace(marketplaceId)?.name ?? ''
        });
    };

    const closeInvoiceDetailHandler = () => {
        returnToInvoiceDetailRef.current = null;
        setSelectedInvoiceDetail(null);
        dispatch(cleanInvoice());
        options?.onClosed?.();
    };

    const checkoutHandler = (orderId: number | undefined, bookingId: number | undefined) => {
        if (isCheckoutOpen) {
            return;
        }

        if (!orderId && !bookingId) {
            dispatch(showError('Unable to open checkout for this invoice.'));
            return;
        }

        setShowCheckout({
            orderId: orderId ?? undefined,
            bookingId: bookingId ?? undefined,
            hide: false
        });
    };

    const closeCheckoutHandler = () => {
        const returnToInvoiceDetail = returnToInvoiceDetailRef.current;
        returnToInvoiceDetailRef.current = null;
        setShowCheckout(null);
        options?.onClosed?.();

        if (returnToInvoiceDetail) {
            setSelectedInvoiceDetail(returnToInvoiceDetail);
        }
    };

    const checkoutFromInvoiceDetailHandler = (bookingId: number | undefined, orderId: number | undefined) => {
        if (!selectedInvoiceDetail) {
            return;
        }

        returnToInvoiceDetailRef.current = selectedInvoiceDetail;
        setSelectedInvoiceDetail(null);
        dispatch(cleanInvoice());
        checkoutHandler(orderId, bookingId);
    };

    const invoiceDetailView: ReactNode = (
        <>
            {showCheckout && (
                <Checkout
                    orderId={showCheckout.orderId}
                    bookingId={showCheckout.bookingId}
                    onClose={closeCheckoutHandler}
                    onPayByLinkSent={closeCheckoutHandler}
                    onBack={closeCheckoutHandler}
                    hide={showCheckout.hide}
                    zIndex={options?.zIndex}
                />
            )}
            {selectedInvoiceDetail && !isCheckoutOpen && (
                <InvoiceDetail
                    invoiceId={selectedInvoiceDetail.invoiceId}
                    marketplaceId={selectedInvoiceDetail.marketplaceId}
                    marketplaceName={selectedInvoiceDetail.marketplaceName}
                    onClose={closeInvoiceDetailHandler}
                    onCheckout={checkoutFromInvoiceDetailHandler}
                    zIndex={options?.zIndex}
                />
            )}
        </>
    );

    return { openInvoiceDetail, invoiceDetailView };
};
